import { AlertTriangle, Loader2 } from 'lucide-react'

import type { StockShortfallItem } from '@/lib/api/sales-order-api'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { m } from '@/lib/paraglide/messages'

// FE dùng UnitLookup để bổ sung cột "ĐVT" khi render bảng — BE trả shortfalls
// chỉ có product_id/name/available/requested. Đơn vị lấy từ form state của
// order (SalesOrderItem.unit) hoặc từ product picker, tuỳ nguồn.
type UnitLookup = Record<string, string | undefined>

interface AwaitingPackagingDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    shortfalls: StockShortfallItem[]
    unitByProductId?: UnitLookup
    onConfirm: () => void
    submitting?: boolean
}

export function AwaitingPackagingDialog({
    open,
    onOpenChange,
    shortfalls,
    unitByProductId,
    onConfirm,
    submitting = false,
}: AwaitingPackagingDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="size-5 text-amber-500" />
                        {m.await_dialog_title()}
                    </DialogTitle>
                </DialogHeader>

                <p className="text-sm text-muted-foreground">
                    {m.await_dialog_description({
                        count: shortfalls.length,
                    })}
                </p>

                <div className="overflow-x-auto rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12 text-center">
                                    {m.await_table_col_no()}
                                </TableHead>
                                <TableHead>
                                    {m.await_table_col_product()}
                                </TableHead>
                                <TableHead className="w-16 text-center">
                                    {m.await_table_col_unit()}
                                </TableHead>
                                <TableHead className="w-24 text-right">
                                    {m.await_table_col_requested()}
                                </TableHead>
                                <TableHead className="w-24 text-right">
                                    {m.await_table_col_available()}
                                </TableHead>
                                <TableHead className="w-24 text-right font-semibold text-destructive">
                                    {m.await_table_col_missing()}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {shortfalls.map((sf, idx) => {
                                const unit =
                                    unitByProductId?.[sf.product_id] ?? '—'
                                const missing = sf.requested - sf.available
                                return (
                                    <TableRow key={sf.product_id}>
                                        <TableCell className="text-center text-muted-foreground">
                                            {idx + 1}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {sf.product_name}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {unit}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {sf.requested}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-muted-foreground">
                                            {sf.available}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums font-semibold text-destructive">
                                            {missing}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                    >
                        {m.await_dialog_cancel()}
                    </Button>
                    <Button
                        type="button"
                        onClick={onConfirm}
                        disabled={submitting}
                    >
                        {submitting && (
                            <Loader2 className="mr-2 size-4 animate-spin" />
                        )}
                        {submitting
                            ? m.await_dialog_confirming()
                            : m.await_dialog_confirm()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
